'use client';

import Image from 'next/image';
import { useState, useCallback, useRef, useEffect } from 'react';
import { Mail, Calendar, User, X } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { AgentMeetingModal } from '@/components/agents/AgentMeetingModal';
import type { Agent } from '@/components/agents/AgentsSection';
import { analyticsEvents } from '@/components/analytics/GoogleAnalytics';

export interface ListingAgent {
  id: string;
  name: string;
  email: string;
  avatar: string | null;
  jobTitle: string | null;
}

interface AllysAgentContactPopoverProps {
  agent: ListingAgent;
  organisationId?: string;
  /** Position the popover relative to the trigger */
  position?: 'above' | 'below';
  children: React.ReactNode;
}

/**
 * Wraps a trigger element (the agent avatar) and shows a contact card on click.
 * Stops event propagation so the parent Link doesn't navigate.
 */
export function AllysAgentContactPopover({
  agent,
  organisationId = '',
  position = 'above',
  children,
}: AllysAgentContactPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  const handleToggle = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen((prev) => !prev);
  }, []);

  const handleClose = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(false);
  }, []);

  const handleEmailClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    analyticsEvents.clickToEmail(agent.email, `listing_agent_${agent.name}`);
    const subject = encodeURIComponent(`Property Inquiry - ${agent.name}`);
    const body = encodeURIComponent(
      `Hello ${agent.name.split(' ')[0]},\n\nI would like to discuss a property with you.\n\nBest regards`
    );
    window.location.href = `mailto:${agent.email}?subject=${subject}&body=${body}`;
    setIsOpen(false);
  }, [agent.email, agent.name]);

  const handleScheduleClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    analyticsEvents.scheduleMeetingIntent(agent.name, 'listing_card_agent');
    setShowMeetingModal(true);
    setIsOpen(false);
  }, [agent.name]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Adapt the listing agent to the Agent interface for AgentMeetingModal
  const meetingAgent: Agent = {
    id: agent.id,
    name: agent.name,
    email: agent.email,
    image: agent.avatar ?? '',
    ...(agent.jobTitle != null && { title: agent.jobTitle }),
  };

  const isAbove = position === 'above';

  return (
    <>
      <div ref={popoverRef} className="relative">
        {/* Trigger — the agent avatar */}
        <button
          type="button"
          onClick={handleToggle}
          className="cursor-pointer appearance-none border-0 bg-transparent p-0 m-0"
          aria-label={`Contact ${agent.name}`}
          aria-expanded={isOpen}
        >
          {children}
        </button>

        {/* Popover */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: isAbove ? 8 : -8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: isAbove ? 8 : -8 }}
              transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className={`
                absolute z-50 w-[min(16rem,calc(100vw-2rem))] right-0
                ${isAbove ? 'bottom-full mb-3' : 'top-full mt-3'}
              `}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
            >
              <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden font-inter">
                {/* Header with agent info */}
                <div className="relative bg-gradient-to-br from-[#243462] to-[#1a2847] px-4 pt-4 pb-5">
                  {/* Close button */}
                  <button
                    type="button"
                    onClick={handleClose}
                    className="absolute top-2 right-2 w-8 h-8 min-w-[44px] min-h-[44px] rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors cursor-pointer"
                    aria-label="Close"
                  >
                    <X size={12} className="text-white/80" />
                  </button>

                  <div className="flex items-center gap-3">
                    {agent.avatar ? (
                      <div className="w-12 h-12 rounded-full overflow-hidden ring-2 ring-white/30 flex-shrink-0">
                        <Image
                          src={agent.avatar}
                          alt={agent.name}
                          width={48}
                          height={48}
                          className="object-cover object-top w-full h-full"
                        />
                      </div>
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-[#BBA339] ring-2 ring-white/30 flex items-center justify-center flex-shrink-0">
                        <User size={20} className="text-white" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-white truncate">{agent.name}</p>
                      {agent.jobTitle && (
                        <p className="text-xs text-[#BBA339] font-medium truncate">{agent.jobTitle}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="p-3 space-y-2">
                  {/* Email */}
                  <button
                    type="button"
                    onClick={handleEmailClick}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-blue-50 hover:bg-blue-100 transition-colors cursor-pointer group/btn"
                  >
                    <div className="w-9 h-9 rounded-lg bg-blue-500 flex items-center justify-center flex-shrink-0 shadow-sm shadow-blue-500/20 group-hover/btn:scale-105 transition-transform">
                      <Mail size={16} className="text-white" />
                    </div>
                    <div className="text-left min-w-0">
                      <p className="text-sm font-semibold text-gray-900">Email</p>
                      <p className="text-xs text-gray-500 truncate">{agent.email}</p>
                    </div>
                  </button>

                  {/* Schedule Meeting */}
                  <button
                    type="button"
                    onClick={handleScheduleClick}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-violet-50 hover:bg-violet-100 transition-colors cursor-pointer group/btn"
                  >
                    <div className="w-9 h-9 rounded-lg bg-violet-500 flex items-center justify-center flex-shrink-0 shadow-sm shadow-violet-500/20 group-hover/btn:scale-105 transition-transform">
                      <Calendar size={16} className="text-white" />
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-semibold text-gray-900">Schedule Meeting</p>
                      <p className="text-xs text-gray-500">Book a time that works</p>
                    </div>
                  </button>
                </div>

                {/* Arrow */}
                <div className={`absolute right-4 ${isAbove ? 'top-full' : 'bottom-full'}`}>
                  <div className={`w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent ${
                    isAbove
                      ? 'border-t-[8px] border-t-white'
                      : 'border-b-[8px] border-b-[#243462]'
                  }`} />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Meeting Modal — rendered at portal level */}
      <AnimatePresence>
        {showMeetingModal && (
          <AgentMeetingModal
            agent={meetingAgent}
            organisationId={organisationId}
            onClose={() => setShowMeetingModal(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
}
